import React, { MouseEvent } from 'react'
import styled from 'styled-components'
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay'
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause'
import { faBackward } from '@fortawesome/pro-solid-svg-icons/faBackward'
import { faForward } from '@fortawesome/pro-solid-svg-icons/faForward'
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat'

import Slider from './Slider'
import VolumeControl from './VolumeControl'
import BandWidthControl from './BandWidthControl'
import Box from 'components/Box'
import { HexToRgba } from 'utils/colors'
import Text from 'components/Text'
import {
  isAudioCorrupted,
  isAudioLoading,
  isAudioProbingSSL,
  isAudioRepeatable,
  isAudioSSLErrorFallback,
} from './helper'
import Icon from 'components/Icon'

const step = 15
const format = (sec: number) =>
  isNaN(sec)
    ? '-:-'
    : `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(Math.round(sec % 60)).padStart(2, '0')}`

interface PlayerLayoutProps {
  errorCode: number | null
  isFloating?: boolean
  isPlaying: boolean
  currentTime: number
  duration: number
  buffered: number
  src?: string | null
  fallbackMessage: string | null
  toggle: () => void
  seek: (time: number) => void
  changeVolume: (v: number) => void
  changeSpeed: (s: number) => void
}

const PlayerLayout: React.FC<PlayerLayoutProps> = ({
  toggle,
  seek,
  changeVolume,
  changeSpeed,
  src,
  errorCode,
  isPlaying,
  currentTime,
  duration,
  buffered,
  fallbackMessage,
  isFloating = false,
}) => {
  if (!isFloating && !src) return null

  const handleClick = (e: MouseEvent<HTMLElement>) => e.stopPropagation()
  const renderTrack = () => {
    if (isAudioLoading({ fallbackMessage })) {
      return <LoadingSlider height="12px" width="100px" />
    } else if (isAudioRepeatable({ errorCode })) {
      return <Box height="12px" width="100px" backgroundColor={HexToRgba('#E24C4C', 0.15)} />
    } else {
      const clampedValue = duration && currentTime > duration ? duration : currentTime
      return (
        <Slider
          max={!duration ? NaN : duration}
          min={0}
          value={clampedValue}
          buffered={buffered}
          onChange={seek}
          isFloating={isFloating}
        />
      )
    }
  }

  const renderCorruptPlayer = () => {
    const isCorrupt = isAudioCorrupted({ errorCode })
    const isSSLError = isAudioSSLErrorFallback(fallbackMessage)
    const isProbingSSL = isAudioProbingSSL({ errorCode, fallbackMessage })
    if (isProbingSSL || (!isCorrupt && !isSSLError)) {
      return null
    }
    const overlayMessage = isSSLError ? fallbackMessage : 'live_call_recover_recording_error'
    return (
      <Box
        backgroundColor="rgba(255, 255, 255, 0.8)"
        zIndex={1}
        position="absolute"
        height="28px"
        width="100%"
        top={0}
        left={0}
        display="flex"
        gap="0"
      >
        <Text textAlign="center" color="gray7" margin="5px auto">
          {overlayMessage}
        </Text>
      </Box>
    )
  }

  return (
    <>
      <FlexBox onClick={handleClick} isFloating={isFloating}>
        {renderCorruptPlayer()}
        <Controls>
          <button onClick={() => seek(currentTime - step)}>
            <Icon icon={faBackward} color={isFloating ? 'white' : 'grayShadeBlack2'} />
          </button>
          <button onClick={toggle}>
            {isAudioRepeatable({ errorCode }) ? (
              <Icon icon={faRepeat} color={isFloating ? 'white' : 'gray7'} />
            ) : isAudioLoading({ fallbackMessage }) ? (
              <Icon
                data-cy={isAudioLoading({ fallbackMessage })}
                data-testid="play-audio"
                icon={faPlay}
                color="gray6"
              />
            ) : isPlaying ? (
              <Icon data-testid="pause-audio" icon={faPause} color={isFloating ? 'white' : 'gray7'} />
            ) : (
              <Icon
                data-cy={isAudioLoading({ fallbackMessage })}
                data-testid="play-audio"
                icon={faPlay}
                color={isFloating ? 'white' : 'gray7'}
              />
            )}
          </button>
          <button onClick={() => seek(currentTime + step)}>
            <Icon icon={faForward} color={isFloating ? 'white' : 'grayShadeBlack2'} />
          </button>
        </Controls>
        <SeekerWrapper>
          <TimeText isFloating={isFloating}>{format(currentTime)}</TimeText>
          {renderTrack()}
          <TimeText isFloating={isFloating}>{format(duration)}</TimeText>
        </SeekerWrapper>
        <VolumeControl onChange={changeVolume} isFloating={isFloating} />
        <BandWidthControl onChange={(s: any) => changeSpeed(Number(s))} isFloating={isFloating} />
      </FlexBox>
      {isAudioRepeatable({ errorCode }) && (
        <Text mt="4px" color="red" fontSize="12px" fontWeight="medium" lineHeight="16px">
          audio_network_error_msg
        </Text>
      )}
    </>
  )
}

export default React.memo(PlayerLayout)

const FlexBox = styled.div<{ isFloating: boolean }>`
  padding: ${({ isFloating }) => (!isFloating ? '0 12px' : '')};
  border-radius: 4px;
  height: ${({ isFloating }) => (!isFloating ? '28px' : '17px')};
  display: flex;
  align-items: center;
  position: relative;
  background: ${({ isFloating }) => (!isFloating ? 'linear-gradient(180deg, #e7ebf3 0%, #c7cedc 100%)' : 'black')};
`

const Controls = styled.div`
  display: flex;
`

const SeekerWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`

const TimeText = styled.span<{ isFloating: boolean }>`
  margin: 0 10px;
  font-size: 14px;
  color: ${({ isFloating, theme }) => (isFloating ? 'white' : theme.colors.gray14)};
`

const LoadingSlider = styled(Box)`
  ${({ theme }) => `
  background-image: repeating-linear-gradient(110deg, ${theme.colors.gray6}, ${theme.colors.gray6} 6px, transparent 3px, transparent 12px);
  background-size: 100% 100%;
  
  `}
`
